//@flow
import * as React from 'react';
import { Box, Subheading } from '@locus-taxy/neo-ui';
import { EditableInputField } from 'common/components/core/common/CustomEditableField';
import theme from './ItemCard.scss';
import StringUtils from 'common/utils/StringUtils';
import { useTranslation } from 'react-i18next';

type Props = {
    entity: { id: string, name?: string, code?: string },
    onItemClick?: (id: string) => void,
    selected: boolean,
    isEditable?: boolean,
    hovered: boolean,
    cardColor: ?string,
    setHoveredEntity: (id?: string) => void,
    changeTerritoryName?: (id: string, name: string) => void,
    subHeadingLabel: string
};

const ItemCard = (props: Props): React.Node => {
    const {
        onItemClick,
        entity: { id, name },
        selected,
        cardColor,
        setHoveredEntity,
        isEditable,
        changeTerritoryName
    } = props;

    const { t } = useTranslation('entityManagement');

    const onHover = React.useCallback(() => {
        setHoveredEntity(id);
    }, [id, setHoveredEntity]);

    const onHoverOut = React.useCallback(() => {
        setHoveredEntity(null);
    }, [setHoveredEntity]);

    const onClick = React.useCallback(() => {
        onItemClick?.(id);
    }, [id, onItemClick]);

    const onSave = React.useCallback(
        value => {
            return new Promise((resolve, reject) => {
                if (StringUtils.isEmpty(value)) {
                    reject({
                        message: t('createTerritory.territoryPlanNameEmpty')
                    });
                    return;
                }
                changeTerritoryName?.(id, value);
                resolve();
            });
        },
        [changeTerritoryName, id, t]
    );

    return (
        <Box
            onClick={onClick}
            p="12px"
            sx={{
                ':hover': {
                    bg: cardColor ? cardColor : 'initial',
                    '.textHover, .editableInputField div p': {
                        color: 'white'
                    }
                },
                cursor: 'pointer',
                '.editableInputField': {
                    'div p': {
                        fontSize: 2,
                        color: selected ? 'white' : 'black'
                    }
                }
            }}
            borderRadius="2px"
            border="1px solid"
            borderColor="grey.200"
            bg={selected ? cardColor : 'initial'}
            onMouseOver={onHover}
            onMouseOut={onHoverOut}
        >
            {name && (
                <EditableInputField
                    activateOnFieldClick={isEditable}
                    theme={theme}
                    className={`${theme.editableInputField} editableInputField`}
                    type="text"
                    value={name}
                    isEditingEnabled={isEditable}
                    onSave={onSave}
                    isLoading={false}
                />
            )}
            {id && (
                <Subheading
                    className="textHover"
                    color={selected ? 'white' : 'black'}
                    truncated
                    title={id}
                >
                    {props.subHeadingLabel}: {id}
                </Subheading>
            )}
        </Box>
    );
};

export default ItemCard;
